import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Bell } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useAuthStore } from '@features/authentication/store/authStore';
import { PERMISSIONS } from '@features/shared/constants/permissions';
import {
  getPushStatus,
  isPushSupported,
  registerPush,
  unsubscribePush,
  type PushStatus,
} from '@/lib/pushNotifications';

// CSC-295 — Notifications card on the profile page. Single on/off toggle for
// "New RFQ notifications" — no per-event matrix in v1. The switch reflects
// pushNotifications status (the single source of truth) rather than holding
// its own; this avoids drift if the user revokes via browser site settings.
//
// Permission-gated identically to the bell + onboarding banner. Hidden
// entirely when the user lacks `quotations.review_inbox`.
export function NotificationsCard() {
  const hasReviewInbox = useAuthStore((s) =>
    s.hasPermission(PERMISSIONS.QUOTATIONS_REVIEW_INBOX),
  );
  const [status, setStatus] = useState<PushStatus>('off');
  const [isWorking, setIsWorking] = useState(false);

  useEffect(() => {
    if (!hasReviewInbox) return;
    let cancelled = false;
    void getPushStatus().then((next) => {
      if (!cancelled) setStatus(next);
    });
    return () => {
      cancelled = true;
    };
  }, [hasReviewInbox]);

  if (!hasReviewInbox) return null;

  const supported = isPushSupported();
  const isOn = status === 'on';
  const isBlocked = status === 'denied';

  async function handleToggle(checked: boolean) {
    setIsWorking(true);
    try {
      if (checked) {
        const next = await registerPush();
        setStatus(next);
        if (next === 'on') toast.success('Desktop notifications enabled');
        else if (next === 'denied') {
          toast.error(
            'Notifications were blocked. Re-enable them in your browser site settings to opt in.',
          );
        }
      } else {
        const next = await unsubscribePush();
        setStatus(next);
        toast.success('Desktop notifications disabled');
      }
    } catch (err) {
      console.error('Push toggle failed:', err);
      toast.error('Could not update notifications');
    } finally {
      setIsWorking(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Bell className="h-4 w-4" />
          Notifications
        </CardTitle>
        <CardDescription>
          Choose how DOP alerts you. Desktop notifications require browser permission.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-0.5">
            <Label htmlFor="rfq-push-toggle" className="text-sm font-medium">
              New RFQ notifications
            </Label>
            <p className="text-xs text-muted-foreground">
              Get a desktop toast when a new RFQ email lands in the inbox — even when
              this tab is closed. Limited to Chrome / Edge / Firefox; iOS Safari requires
              installing DOP as a PWA.
            </p>
            {isBlocked && (
              <p className="text-xs text-warning">
                Notifications are blocked at the browser level. Re-enable via your
                browser's site settings, then toggle this switch.
              </p>
            )}
            {!supported && (
              <p className="text-xs text-muted-foreground">
                This browser doesn't support Web Push notifications.
              </p>
            )}
          </div>
          <Switch
            id="rfq-push-toggle"
            checked={isOn}
            disabled={!supported || isBlocked || isWorking}
            onCheckedChange={handleToggle}
            aria-label="Toggle new RFQ desktop notifications"
          />
        </div>
      </CardContent>
    </Card>
  );
}
